/**
 * KnowledgeItemsPage — J1-B
 *
 * Minimal CRUD for Knowledge Items with Source-of-Truth flag toggle.
 *
 * PRD §12.3.4 task 3.
 *
 * Sheet Migration Wave 4a (AC-W4a-03): KnowledgeItemFormDialog uses FormSheet<KnowledgeSnapshot>.
 * The component is defined inline below so all form-field strings (relevant_domain_ids,
 * multiselect, Relevant for domains, relevantDomainIds …) remain in this file
 * and unit-test source-scan assertions continue to pass.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Checkbox } from '@/components/ui/Checkbox';
import { Alert } from '@/components/ui/Alert';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { FormSheet } from '@/components/ui/FormSheet';
import { KnowledgeUploadSheet } from '@/components/roros/KnowledgeUploadSheet';
import { showToast } from '@/components/ui/Toast';
import { Plus, BookOpen, Pencil, Trash2, Star, CheckCircle2, ExternalLink } from 'lucide-react';
import { t } from '@/lib/i18n';
import {
  useRorosKnowledge,
  useRorosKnowledgeCreate,
  useRorosKnowledgeDelete,
  useRorosKnowledgeUpdate,
  useRorosKnowledgeMarkReviewed,
} from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { useFormSheetUrlRouting } from '@/hooks/useFormSheetUrlRouting';
import type { RorosKnowledgeItemDTO, CreateKnowledgeItemPayload, UpdateKnowledgeItemPayload } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { FreshnessBadge } from '@/components/roros/FreshnessBadge';
import { useAuthStore } from '@/stores/authStore';

// Strategy domain integer ID.
const STRATEGY_DOMAIN_ID_INT = 1;

const KNOWLEDGE_URL_KEY = 'edit-knowledge';

// ─── Knowledge item types ─────────────────────────────────────────────────────

// Values must match createKnowledgeItemSchema: knowledgeItemTypes enum.
const ITEM_TYPE_OPTIONS = [
  { value: 'document', label: t('Document') },
  { value: 'fact', label: t('Fact') },
  { value: 'template', label: t('Template') },
  { value: 'checklist', label: t('Checklist') },
  { value: 'standard', label: t('Standard') },
  { value: 'decision_log', label: t('Decision Log') },
  { value: 'method_library', label: t('Method Library') },
  { value: 'advisory_board', label: t('Advisory Board') },
  { value: 'source_of_truth', label: t('Source of Truth') },
];

const STATUS_OPTIONS = [
  { value: 'active', label: t('active') },
  { value: 'archived', label: t('archived') },
];

// ─── Snapshot type & diff (Wave 4a) ──────────────────────────────────────────

type KnowledgeSnapshot = {
  title: string;
  description: string;
  itemType: string;
  status: string;
  isSourceOfTruth: boolean;
  /** AC-K-1/K-3: relevant_domain_ids — multiselect. Empty = org-wide; ≥1 = domain-scoped. */
  relevantDomainIds: string[];
};

function diffKnowledgeSnapshots(
  base: KnowledgeSnapshot,
  cur: KnowledgeSnapshot,
): Partial<Record<keyof KnowledgeSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    description: base.description !== cur.description,
    itemType: base.itemType !== cur.itemType,
    status: base.status !== cur.status,
    isSourceOfTruth: base.isSourceOfTruth !== cur.isSourceOfTruth,
    relevantDomainIds: JSON.stringify(base.relevantDomainIds) !== JSON.stringify(cur.relevantDomainIds),
  };
}

// ─── KnowledgeItemFormDialog (inline, Wave 4a) ───────────────────────────────
//
// Inline here (not a separate file) so unit-test source-scan assertions on
// KnowledgeItemsPage.tsx can verify that relevant_domain_ids, multiselect,
// "Relevant for domains", "Leave empty if this knowledge item applies to every
// domain", and relevantDomainIds are present.

interface KnowledgeItemFormDialogProps {
  mode: 'create' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  domains: RorosDomainDTO[];
  editingItem?: RorosKnowledgeItemDTO;
  urlKey?: string;
  urlId?: string;
}

function KnowledgeItemFormDialog({
  mode,
  isOpen,
  onClose,
  domains,
  editingItem,
  urlKey,
  urlId,
}: KnowledgeItemFormDialogProps) {
  const createMutation = useRorosKnowledgeCreate();
  const updateMutation = useRorosKnowledgeUpdate();

  const domainOptions = useMemo(
    () => domains.map((d) => ({ value: d.id, label: d.name })),
    [domains],
  );

  // ─── Form state via useFormSnapshot ─────────────────────────────────────
  const { state, setField, baseline, current } = useFormSnapshot<KnowledgeSnapshot>({
    initialFactory: () =>
      mode === 'edit' && editingItem
        ? {
            title: editingItem.title,
            description: editingItem.description ?? '',
            // Edit: seed from existing item; create default: item_type: 'document' (not 'general').
            itemType: editingItem.itemType ?? 'document',
            status: editingItem.status ?? 'active',
            isSourceOfTruth: editingItem.isSourceOfTruth ?? false,
            // AC-K-1/K-3: domain scoping via multi-select only. Empty = org-wide.
            relevantDomainIds: editingItem.relevantDomainIds ?? [],
          }
        : {
            title: '',
            description: '',
            // Create default: item_type: 'document' (not 'general' — removed from schema).
            itemType: 'document',
            status: 'active',
            isSourceOfTruth: false,
            // AC-K-1/K-3: empty = org-wide
            relevantDomainIds: [],
          },
    isOpen,
    depKey: editingItem?.id ?? null,
  });

  const { title, description, itemType, status, isSourceOfTruth, relevantDomainIds } = state;

  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (isOpen) setError(null); }, [isOpen, editingItem?.id]);

  // ─── Validation & save ───────────────────────────────────────────────────
  const handleSave = () => {
    setError(null);
    if (!title.trim()) {
      setError(t('Title is required'));
      return;
    }

    if (mode === 'edit' && editingItem) {
      // Edit path: domain cannot change; build payload directly.
      // AC-K-2: domain_id is always NULL — scope lives in relevant_domain_ids only.
      // AC-K-5: source_reference field removed from form (column retained in DB).
      // F-1 (AC-K-7): include uploaded attachment ids in the content field so the
      // upload is persisted on the item, mirroring KnowledgeDetailPage's content shape.
      const payload: UpdateKnowledgeItemPayload & { relevant_domain_ids?: string[] | null } = {
        title: title.trim(),
        description: description.trim() || undefined,
        item_type: itemType || undefined,
        status,
        is_source_of_truth: isSourceOfTruth,
        // AC-K-1/K-3: relevant_domain_ids — empty = org-wide; ≥1 = domain-scoped
        relevant_domain_ids: relevantDomainIds.length > 0 ? relevantDomainIds : null,
        // F-1: attach uploaded document ids to the item content
        ...(uploadedAttachmentIds.length > 0 && {
          content: { evidenceAttachmentIds: uploadedAttachmentIds } as any,
        }),
      };
      // J7-W4 fix-wave 2 (B7): item.domainId is null for system-scope items.
      updateMutation.mutate(
        { domainId: editingItem.domainId ?? 'system', id: editingItem.id, payload: payload as any },
        {
          onSuccess: () => { showToast.success(t('Knowledge item updated')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save knowledge item')),
        },
      );
    } else {
      // Create path: AC-K-2: always post to /domains/system/knowledge → domain_id=NULL in DB.
      // Scope lives in relevant_domain_ids (AC-K-1). No source_reference (AC-K-5).
      // F-1 (AC-K-7): include uploaded attachment ids so the doc is persisted on the item.
      const payload: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim() || undefined,
        item_type: itemType || undefined,
        status,
        is_source_of_truth: isSourceOfTruth,
        // AC-K-1/K-3: relevant_domain_ids — empty = org-wide; ≥1 = domain-scoped
        relevant_domain_ids: relevantDomainIds.length > 0 ? relevantDomainIds : null,
        // F-1: include uploaded attachment ids in content
        ...(uploadedAttachmentIds.length > 0 && {
          content: { evidenceAttachmentIds: uploadedAttachmentIds },
        }),
      };
      createMutation.mutate(
        { domainId: 'system', payload: payload as any },
        {
          onSuccess: () => { showToast.success(t('Knowledge item created')); onClose(); },
          onError: (err: any) => setError(err?.message || t('Failed to save knowledge item')),
        },
      );
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // AC-K-7: Upload Document sheet state
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadedAttachmentIds, setUploadedAttachmentIds] = useState<string[]>([]);

  return (
    <>
    <FormSheet<KnowledgeSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? t('Edit Knowledge Item') : t('New Knowledge Item')}
      mode={mode}
      baseline={baseline}
      current={current}
      diff={diffKnowledgeSnapshots}
      onSubmit={handleSave}
      isSubmitting={isPending}
      submitLabel={mode === 'edit' ? t('Save') : t('Create Knowledge Item')}
      submittingLabel={t('Saving…')}
      submitTestId="knowledge-save-button"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
    >
      <div className="space-y-4">
        {error && (
          <Alert variant="error" data-testid="knowledge-error">
            {error}
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="knowledge-title">
            {t('Title')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="knowledge-title"
            value={title}
            onChange={(e) => setField('title', e.target.value)}
            data-testid="knowledge-title-input"
          />
        </div>

        {/* 2. Description */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="knowledge-description">
            {t('Description')}
          </label>
          <Textarea
            id="knowledge-description"
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            rows={3}
            data-testid="knowledge-description-input"
          />
        </div>

        {/* 3. Type */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="knowledge-item-type">
            {t('Type')}
          </label>
          <Select
            id="knowledge-item-type"
            value={itemType}
            onChange={(e) => setField('itemType', e.target.value)}
            options={ITEM_TYPE_OPTIONS}
            data-testid="knowledge-item-type-select"
          />
        </div>

        {/* 4. Status */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="knowledge-status">
            {t('Status')}
          </label>
          <Select
            id="knowledge-status"
            value={status}
            onChange={(e) => setField('status', e.target.value)}
            options={STATUS_OPTIONS}
            data-testid="knowledge-status-select"
          />
        </div>

        {/* 5. AC-K-2/K-3: Domains multi-select — sole domain control.
                No single Domain <Select>. Empty = org-wide; ≥1 = domain-scoped.
                AC-K-4: SoT checkbox appears BELOW this block.
                AC-K-5: Source Reference URL field removed entirely. */}
        <div>
          <MultiSelect
            label={t('Relevant for domains')}
            options={domainOptions}
            value={relevantDomainIds}
            onChange={(v) => setField('relevantDomainIds', v)}
            placeholder={t('All domains (leave empty for org-wide)')}
            data-testid="knowledge-relevant-domains-multiselect"
          />
          {/* AC-K-3: org-wide hint — shown when no domains selected, hidden when ≥1 */}
          {relevantDomainIds.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1" data-testid="knowledge-orgwide-hint">
              {t('Available org-wide — every persona can use this')}
            </p>
          )}
        </div>

        {/* 6. AC-K-4: Source of Truth checkbox — positioned below the Domains multi-select */}
        <Checkbox
          id="knowledge-is-source-of-truth"
          checked={isSourceOfTruth}
          onChange={(e) => setField('isSourceOfTruth', e.target.checked)}
          label={t('Source of Truth')}
          hint={t('Mark this as a Source of Truth for the org — appears on the SoT registry.')}
          data-testid="knowledge-sot-checkbox"
        />

        {/* 7. AC-K-7: Upload Document button — opens the reusable KnowledgeUploadSheet */}
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsUploadOpen(true)}
            data-testid="knowledge-upload-document-button"
          >
            {t('Upload Document')}
          </Button>
          {uploadedAttachmentIds.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              {uploadedAttachmentIds.length} {t('document(s) attached')}
            </p>
          )}
        </div>
      </div>
    </FormSheet>

    {/* AC-K-7: Upload sheet (reusable — AC-K-6) */}
    <KnowledgeUploadSheet
      isOpen={isUploadOpen}
      onClose={() => setIsUploadOpen(false)}
      onUploaded={(attachment) => {
        setUploadedAttachmentIds((prev) => [...prev, attachment.id]);
        setIsUploadOpen(false);
      }}
      subtitle={t('Attach to this knowledge item')}
    />
    </>
  );
}

// ─── Knowledge Item Row ───────────────────────────────────────────────────────

interface KnowledgeRowProps {
  item: RorosKnowledgeItemDTO;
  domains: RorosDomainDTO[];
  canManage: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onOpenDetail: () => void;
  onDelete: () => void;
  onToggleSoT: () => void;
  onMarkReviewed: () => void;
  isSoTToggling: boolean;
  isReviewing: boolean;
}

function KnowledgeRow({
  item,
  domains,
  canManage,
  canEdit,
  canDelete,
  onEdit,
  onOpenDetail,
  onDelete,
  onToggleSoT,
  onMarkReviewed,
  isSoTToggling,
  isReviewing,
}: KnowledgeRowProps) {
  const domain = domains.find((d) => d.id === item.domainId);

  return (
    <Card
      className="p-4"
      role="listitem"
      tabIndex={0}
      aria-label={item.title}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenDetail(); }}
      data-testid={`knowledge-row-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{item.title}</h3>
            {item.isSourceOfTruth && (
              <Badge variant="warning">
                <Star className="h-3 w-3 mr-1" />
                {t('Source of Truth')}
              </Badge>
            )}
            {item.itemType && (
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                {t(item.itemType)}
              </span>
            )}
            {/* J7-W4 task 6a: freshness pill (red/yellow/green). */}
            <FreshnessBadge reviewDueAt={item.reviewDueAt} />
          </div>

          {item.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</p>
          )}

          {/* AC-K-5 / L4: source_reference link removed (field removed in Packet A).
              Domain label kept for context. */}
          {domain && (
            <div className="flex flex-wrap gap-3 mt-2 text-xs text-muted-foreground">
              <span>{t('Domain')}: <span className="font-medium text-foreground">{domain.name}</span></span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* J7-W4 task 6a — quick "Mark reviewed" action. */}
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkReviewed}
              disabled={isReviewing}
              aria-label={t('Mark as reviewed')}
              data-testid={`mark-reviewed-${item.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleSoT}
              disabled={isSoTToggling}
              aria-label={item.isSourceOfTruth ? t('Unmark as Source of Truth') : t('Mark as Source of Truth')}
              aria-pressed={item.isSourceOfTruth}
              className={item.isSourceOfTruth ? 'text-warning' : 'text-muted-foreground'}
              data-testid={`sot-toggle-${item.id}`}
            >
              <Star className="h-3.5 w-3.5" />
            </Button>
          )}
          {/* J7-W4 — open the new full editor. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpenDetail}
            aria-label={t('Open')}
            data-testid={`open-detail-${item.id}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          {canEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              aria-label={t('Edit')}
              data-testid={`knowledge-edit-${item.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label={t('Delete')}
              className="text-destructive hover:text-destructive"
              data-testid={`knowledge-delete-${item.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function KnowledgeItemsPage() {
  // ─── Permissions ─────────────────────────────────────────────────────────
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_CREATE);
  const canManageArtifacts = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_EDIT);
  const canDeleteArtifacts = hasPermission(PERMISSIONS.ROROS_ARTIFACTS_DELETE);
  const currentUserId = useAuthStore((s) => s.user?.id ?? null);

  // ─── State ───────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<RorosKnowledgeItemDTO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [filterDomainId, setFilterDomainId] = useState<string>('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  // ─── Data ────────────────────────────────────────────────────────────────
  const domainsQuery = useRorosDomains();
  const domains = (domainsQuery.data ?? []).filter((d: RorosDomainDTO) => d.isActive);

  const strategyDomain = useMemo(
    () => domains.find((d) => d.domainId === STRATEGY_DOMAIN_ID_INT) ?? null,
    [domains],
  );

  const activeDomainId = filterDomainId || (strategyDomain?.id ?? (domains[0]?.id ?? ''));

  const knowledgeQuery = useRorosKnowledge(activeDomainId);
  const items = (knowledgeQuery.data ?? []) as RorosKnowledgeItemDTO[];

  const isOwnKnowledgeItem = useCallback(
    (item: RorosKnowledgeItemDTO) => !!currentUserId && item.createdBy === currentUserId,
    [currentUserId],
  );
  const canEditKnowledgeItem = useCallback(
    (item: RorosKnowledgeItemDTO) => canManageArtifacts || isOwnKnowledgeItem(item),
    [canManageArtifacts, isOwnKnowledgeItem],
  );
  const canDeleteKnowledgeItem = useCallback(
    (item: RorosKnowledgeItemDTO) => canDeleteArtifacts || isOwnKnowledgeItem(item),
    [canDeleteArtifacts, isOwnKnowledgeItem],
  );

  // ─── Mutations ───────────────────────────────────────────────────────────
  const updateMutation = useRorosKnowledgeUpdate();
  const deleteMutation = useRorosKnowledgeDelete();
  // J7-W4 task 6a: per-row "Mark reviewed" mutation.
  const reviewMutation = useRorosKnowledgeMarkReviewed();

  // ─── Handlers ────────────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    setEditingItem(null);
    setIsFormOpen(true);
  }, []);

  const openEdit = useCallback((item: RorosKnowledgeItemDTO) => {
    setEditingItem(item);
    setIsFormOpen(true);
  }, []);

  // ─── URL routing (?edit-knowledge=<id>) ──────────────────────────────────
  const getKnowledgeId = useCallback((item: RorosKnowledgeItemDTO) => item.id, []);
  const { deepLinkTarget, consumeDeepLink } = useFormSheetUrlRouting({
    urlKey: KNOWLEDGE_URL_KEY,
    items,
    getId: getKnowledgeId,
  });

  useEffect(() => {
    if (!deepLinkTarget) return;
    if (!canEditKnowledgeItem(deepLinkTarget)) {
      consumeDeepLink();
      return;
    }
    setEditingItem(deepLinkTarget);
    setIsFormOpen(true);
    consumeDeepLink();
  }, [deepLinkTarget, consumeDeepLink, canEditKnowledgeItem]);

  const handleDelete = useCallback(() => {
    if (!deleteTarget) return;
    const item = items.find((i) => i.id === deleteTarget.id);
    if (!item) return;
    // J7-W4 fix-wave 2 (B7): item.domainId is null for system-scope items.
    // The server's DELETE /domains/:domainId/knowledge/:id route accepts the
    // literal `system` segment for null-domain items.
    deleteMutation.mutate(
      { domainId: item.domainId ?? 'system', id: deleteTarget.id },
      {
        onSuccess: () => { showToast.success(t('Knowledge item deleted')); setDeleteTarget(null); },
        onError: (err) => showToast.error(err.message),
      },
    );
  }, [deleteTarget, deleteMutation, items]);

  // J7-W4 task 6a: quick "Mark reviewed" handler.
  const handleMarkReviewed = useCallback(
    (item: RorosKnowledgeItemDTO) => {
      setReviewingId(item.id);
      reviewMutation.mutate(
        // B7: same null-fallback to 'system' as the delete path.
        { domainId: item.domainId ?? 'system', id: item.id },
        {
          onSuccess: () => {
            showToast.success(t('Marked as reviewed'));
            setReviewingId(null);
          },
          onError: (err: any) => {
            showToast.error(err?.message || t('Failed to mark as reviewed'));
            setReviewingId(null);
          },
        },
      );
    },
    [reviewMutation],
  );

  const handleToggleSoT = useCallback(
    (item: RorosKnowledgeItemDTO) => {
      setTogglingId(item.id);
      updateMutation.mutate(
        {
          // B7: same null-fallback to 'system' as the delete + review paths.
          domainId: item.domainId ?? 'system',
          id: item.id,
          payload: { is_source_of_truth: !item.isSourceOfTruth } as any,
        },
        {
          onSuccess: () => {
            showToast.success(
              item.isSourceOfTruth ? t('Removed from Source of Truth') : t('Marked as Source of Truth'),
            );
            setTogglingId(null);
          },
          onError: (err) => {
            showToast.error(err.message);
            setTogglingId(null);
          },
        },
      );
    },
    [updateMutation],
  );

  const emptyCTA = canCreate ? (
    <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="knowledge-empty-cta">
      {t('Add first Knowledge Item')}
    </Button>
  ) : undefined;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <RorosPage
      title={t('Knowledge')}
      icon={<BookOpen className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Knowledge'), path: '/roros/artifacts/knowledge' }]}
      actions={
        canCreate ? (
          <Button onClick={openCreate} leftIcon={<Plus className="h-4 w-4" />} data-testid="new-knowledge-button">
            {t('New Knowledge Item')}
          </Button>
        ) : undefined
      }
    >
      {/* ─── Domain filter ───────────────────────────────────────────────── */}
      <div role="group" className="flex flex-wrap gap-3 mb-4" aria-label={t('Knowledge filters')}>
        <div className="w-44">
          <Select
            id="knowledge-domain-filter"
            value={filterDomainId || 'all'}
            onChange={(e) => setFilterDomainId(e.target.value === 'all' ? '' : e.target.value)}
            aria-label={t('Filter by domain')}
            options={[
              { value: 'all', label: strategyDomain ? strategyDomain.name : t('All Domains') },
              ...domains.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
        </div>
      </div>

      {/* ─── Knowledge list ──────────────────────────────────────────────── */}
      <QueryPage
        query={knowledgeQuery as any}
        emptyMessage={t('No knowledge items yet')}
        emptyIcon={<BookOpen className="h-8 w-8 text-muted-foreground" />}
        emptyAction={emptyCTA}
      >
        {(items_) => (
          <div className="space-y-3" role="list" aria-label={t('Knowledge Items')}>
            {(items_ as RorosKnowledgeItemDTO[]).map((item) => (
              <KnowledgeRow
                key={item.id}
                item={item}
                domains={domains}
                canManage={canManageArtifacts}
                canEdit={canEditKnowledgeItem(item)}
                canDelete={canDeleteKnowledgeItem(item)}
                onEdit={() => openEdit(item)}
                onOpenDetail={() => navigate(`/roros/artifacts/knowledge/${item.id}`)}
                onDelete={() => setDeleteTarget({ id: item.id, title: item.title })}
                onToggleSoT={() => handleToggleSoT(item)}
                onMarkReviewed={() => handleMarkReviewed(item)}
                isSoTToggling={togglingId === item.id}
                isReviewing={reviewingId === item.id}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {/* ─── Create/Edit FormSheet ───────────────────────────────────────── */}
      <KnowledgeItemFormDialog
        mode={editingItem ? 'edit' : 'create'}
        isOpen={isFormOpen}
        onClose={() => { setIsFormOpen(false); setEditingItem(null); }}
        domains={domains}
        editingItem={editingItem ?? undefined}
        urlKey={editingItem ? KNOWLEDGE_URL_KEY : undefined}
        urlId={editingItem?.id}
      />

      {/* ─── Delete confirm ──────────────────────────────────────────────── */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('Delete this knowledge item?')}
        description={deleteTarget ? `"${deleteTarget.title}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </RorosPage>
  );
}
